import { type ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { Box } from '@/new_src/view/components/Box';
import { Flex } from '@/new_src/view/components/Flex';
import { ScrollArea } from '@/new_src/view/components/ScrollArea';
import { Stack } from '@/new_src/view/components/Stack';

import DeleteButton from 'components/commons/DeleteButton';

import ModalContext from './ModalContext';

import styled from 'styled-components';

type ModalBodyProps = {
  withCloseButton?: boolean;
  withBottomFade?: boolean;
  scrollable?: boolean;
  mah?: number;
  mih?: number;
  gap?: number | string;
  p?: string;
  scrollbarStyles?: React.CSSProperties;
  children: ReactNode;
  onElLoad?(el: HTMLDivElement): void;
};

/**
 * @description
 * withCloseButton: 닫기 버튼을 표시할지 여부 - Modal.Header 컴포넌트가 존재하지 않은 경우 사용
 * withBottomFade: 내용이 잘렸다는 어포던스가 보더가 아니라 fade여야 하는 시안에서 사용
 * mah: Modal Body의 최대 높이 - 최대 높이를 지정하면 스크롤 노출
 * mih: Modal Body의 최소 높이 - 콘텐츠가 적어도 모달이 일정 높이 이상 유지되도록 보장
 * gap: 내부 children 사이의 간격(Stack gap). 미지정 시 Mantine Stack 기본값 사용
 * scrollbarStyles: Mantine ScrollArea의 scrollbar slot에 적용할 CSS. 미지정 시 Mantine 기본
 *   (legacy 모달과 호환되는 투명 트랙/하단 24px 마진이 필요한 경우 명시적으로 전달)
 */
const ModalBody = ({
  withCloseButton = false,
  withBottomFade = false,
  scrollable = true,
  mah,
  mih,
  gap,
  p,
  scrollbarStyles,
  children,
  onElLoad,
}: ModalBodyProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const { onHeaderBorderChange, onFooterBorderChange, onClose } = useContext(ModalContext);

  const handleScroll = () => {
    const el = scrollAreaRef.current;
    if (!el) {
      return;
    }

    const isOverMaxHeight = el.scrollHeight > el.clientHeight;

    if (!isOverMaxHeight) {
      onHeaderBorderChange(false);
      onFooterBorderChange(false);
      setShowBottomFade(false);
      return;
    }

    const isOverScrollTop = el.scrollTop > 0;
    const isOverScrollBottom = el.scrollTop + el.clientHeight < el.scrollHeight;
    onHeaderBorderChange(isOverScrollTop);
    onFooterBorderChange(isOverScrollBottom);
    setShowBottomFade(isOverScrollBottom);
  };

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) {
      return;
    }

    const observer = new ResizeObserver(entries => {
      // DOM 변경 후 레이아웃 루프 오류 방지를 위해 requestAnimationFrame 내부에서 처리
      requestAnimationFrame(() => {
        const isOverMaxHeight = el.scrollHeight > el.clientHeight;
        const isOverScrollBottom = el.scrollTop + el.clientHeight < el.scrollHeight;

        setShowBottomFade(isOverMaxHeight && isOverScrollBottom);

        if (!mah) {
          onFooterBorderChange(isOverMaxHeight && isOverScrollBottom);
        } else {
          for (const entry of entries) {
            const { height } = entry.contentRect;
            if (mah === height) {
              onFooterBorderChange(true);
            }
          }
        }
      });
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Flex
      // 무조건 relative로 두면 본문 내 absolute 요소의 기준이 ModalContent → 이 Flex로 바뀌어 기존 모달 레이아웃이 깨진다
      pos={withBottomFade ? 'relative' : undefined}
      p={withCloseButton ? undefined : p ?? '0 24px'}
      justify="space-between"
      columnGap={12}
      overflow="hidden"
    >
      {scrollable ? (
        <ScrollArea
          viewportRef={el => {
            if (el) {
              scrollAreaRef.current = el;
              onElLoad?.(el);
            }
          }}
          w="100%"
          mah={mah}
          mih={mih}
          p={withCloseButton ? p ?? '24px 0 0 24px' : undefined}
          onScrollPositionChange={handleScroll}
          styles={scrollbarStyles ? { scrollbar: scrollbarStyles } : undefined}
        >
          <Stack w="100%" pb="24px" gap={gap}>
            {children}
          </Stack>
        </ScrollArea>
      ) : (
        <Box w="100%" mah={mah} mih={mih} p={withCloseButton ? p ?? '24px 0 0 24px' : undefined}>
          <Stack w="100%" pb="24px" gap={gap}>
            {children}
          </Stack>
        </Box>
      )}
      {withCloseButton && (
        <Flex p="8px 8px 0 0" h="fit-content">
          <DeleteButton width={48} height={48} onClick={onClose} />
        </Flex>
      )}
      {withBottomFade && showBottomFade && <BottomFade />}
    </Flex>
  );
};

// 공용 Table의 VerticalShadow와 동일 스펙(80px, 6.25% stop)
const BottomFade = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 1;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 80px;
  background: linear-gradient(to top, ${({ theme }) => theme.mantineColors.gray[1]} 6.25%, rgba(255, 255, 255, 0));
`;

export default ModalBody;
